import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CompetitionLifecycleAction } from '../components/CompetitionLifecycleControls';
import { EventWorkspaceSection } from '../components/PageTemplates';
import { useStore } from '../hooks/useStore';
import { useActiveTournament } from '../state/TournamentContext';
import { detectCrewConflicts } from '../services/operationsService';
import { missingPublishFields, publishTournament, updateTournament } from '../services/tournamentService';
import { paths, tournamentSlug } from '../lib/paths';
import {
  AlertTriangle, ArrowUpRight, CheckCircle2, Edit2, Save, X, Rocket,
  Shield, Radio, Users, CalendarDays, ClipboardCheck, CreditCard, MessageSquare, ListChecks,
  type LucideIcon,
} from 'lucide-react';

interface TournamentSetupStep {
  id: string;
  label: string;
  done: boolean;
  hint: string;
  actionLabel: string;
  href?: string;
  onClick?: () => void;
}

/**
 * Tournament Overview tab.
 *
 * Three zones:
 *  1. Inline edit — name, dates, tagline
 *  2. Setup tracker — pizza-tracker style, tells you what's left before you can publish
 *  3. Command center — readiness, blockers, and links into the owner tabs
 */
export function TournamentOverviewPage() {
  const { tournament, divisions, teams, games } = useActiveTournament();
  const navigate = useNavigate();
  const slug = tournamentSlug(tournament);
  const competitionHome = paths.adminCompetition(tournament.format, slug);
  const teamsHref = paths.adminCompetitionTab(tournament.format, slug, 'teams');
  const scheduleHref = paths.adminCompetitionTab(tournament.format, slug, 'schedule');
  const operationsHref = paths.adminCompetitionTab(tournament.format, slug, 'operations');
  const eventCommsHref = paths.adminCompetitionTab(tournament.format, slug, 'comms');
  const registrationHref = `${paths.adminRegistration}?event=${encodeURIComponent(tournament.id)}`;
  const financialsHref = `${paths.adminBilling}?event=${encodeURIComponent(tournament.id)}`;
  const composeCommsHref = `${paths.adminComms}?eventId=${encodeURIComponent(tournament.id)}`;
  const liveGames = games.filter((g) => g.status === 'in_progress');
  const submittedGames = games.filter((g) => g.status === 'final');
  const upcomingGames = games.filter((g) => g.status === 'scheduled' || g.status === 'ready');

  const db = useStore((d) => d);
  const eventRosterEntries = db.rosterEntries.filter((r) => r.tournamentId === tournament.id);
  const rosterTouched = eventRosterEntries.length > 0;
  const eventRegistrations = db.registrations.filter((registration) => registration.tournamentId === tournament.id);
  const eventPayments = db.paymentIntents.filter((payment) => (
    payment.tournamentId === tournament.id ||
    eventRegistrations.some((registration) => registration.id === payment.registrationId || registration.paymentIntentId === payment.id)
  ));
  const paidPayments = eventPayments.filter((payment) => payment.status === 'succeeded');
  const eventCommunications = db.communications.filter((message) => message.tournamentId === tournament.id);
  const sentCommunications = eventCommunications.filter((message) => message.status === 'sent');
  const latestCommunication = [...sentCommunications]
    .sort((a, b) => String(b.sentAt ?? b.updatedAt).localeCompare(String(a.sentAt ?? a.updatedAt)))[0];
  const eventCommunicationIds = new Set(eventCommunications.map((message) => message.id));
  const deliveryIssues = db.communicationDeliveries.filter(
    (delivery) => delivery.communicationId && eventCommunicationIds.has(delivery.communicationId) && (delivery.status === 'failed' || delivery.status === 'skipped'),
  );
  const registrationEnabled = Boolean(tournament.settings.season?.registration?.enabled);
  const missingCrew = useMemo(
    () =>
      games.filter(
        (g) =>
          g.status !== 'final' &&
          !db.gameAssignments.some((a) => a.gameId === g.id && a.position === 'scorekeeper'),
      ),
    [games, db.gameAssignments],
  );
  const conflicts = useMemo(() => detectCrewConflicts(tournament.id), [db, tournament.id]);

  // ── Inline edit ──────────────────────────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(tournament.name);
  const [editTagline, setEditTagline] = useState(tournament.tagline ?? '');
  const [editStart, setEditStart] = useState(toLocal(tournament.startsAt));
  const [editEnd, setEditEnd] = useState(toLocal(tournament.endsAt));

  const openEdit = () => {
    setEditName(tournament.name);
    setEditTagline(tournament.tagline ?? '');
    setEditStart(toLocal(tournament.startsAt));
    setEditEnd(toLocal(tournament.endsAt));
    setEditing(true);
  };

  const saveEdit = () => {
    updateTournament(tournament.id, {
      name: editName.trim() || tournament.name,
      tagline: editTagline.trim() || undefined,
      startsAt: fromLocal(editStart) || tournament.startsAt,
      endsAt: fromLocal(editEnd) || tournament.endsAt,
    });
    setEditing(false);
  };

  // ── Publish ───────────────────────────────────────────────────────────────
  const [publishErr, setPublishErr] = useState<string[] | null>(null);
  const missing = missingPublishFields(tournament);
  const canPublish = missing.length === 0 && tournament.status === 'draft';
  const isPublished =
    tournament.status === 'scheduled' ||
    tournament.status === 'in_progress' ||
    tournament.status === 'paused' ||
    tournament.status === 'archived' ||
    tournament.status === 'completed';

  const onPublish = () => {
    const result = publishTournament(tournament.id);
    if (!result.ok) {
      setPublishErr(result.missing);
    } else {
      setPublishErr(null);
    }
  };

  // ── Setup steps ───────────────────────────────────────────────────────────
  const coreSetupSteps: TournamentSetupStep[] = [
    {
      id: 'basic-info',
      label: 'Basic info',
      done: Boolean(tournament.name && tournament.startsAt && tournament.endsAt),
      hint: 'Name, dates, and venue.',
      actionLabel: 'Edit',
      onClick: openEdit,
    },
    {
      id: 'registration',
      label: 'Registration',
      done: registrationEnabled,
      hint: registrationEnabled ? 'Registration is configured.' : 'Set up registration after the draft exists.',
      actionLabel: registrationEnabled ? 'View' : 'Open registration',
      href: registrationHref,
    },
    {
      id: 'teams',
      label: 'Teams',
      done: teams.length > 0 && rosterTouched,
      hint: teams.length > 0 ? `${teams.length} team${teams.length === 1 ? '' : 's'} and ${eventRosterEntries.length} player${eventRosterEntries.length === 1 ? '' : 's'} added.` : 'Add divisions, teams, and players.',
      actionLabel: teams.length > 0 ? 'View teams' : 'Open teams',
      href: teamsHref,
    },
    {
      id: 'schedule',
      label: 'Schedule',
      done: games.length > 0,
      hint: games.length > 0 ? `${games.length} game${games.length === 1 ? '' : 's'} generated.` : 'Generate the game schedule.',
      actionLabel: games.length > 0 ? 'View schedule' : 'Generate schedule',
      href: scheduleHref,
    },
    {
      id: 'crew',
      label: 'Crew assigned',
      done: missingCrew.length === 0 && games.length > 0,
      hint:
        games.length === 0
          ? 'Generate a schedule first.'
          : missingCrew.length > 0
          ? `${missingCrew.length} game${missingCrew.length === 1 ? '' : 's'} still need a scorekeeper.`
          : 'All games have a scorekeeper.',
      actionLabel: games.length > 0 ? (missingCrew.length > 0 ? 'Assign crew' : 'View crew') : 'Open schedule',
      href: games.length > 0 ? operationsHref : scheduleHref,
    },
  ];
  const nextCoreStep = coreSetupSteps.find((step) => !step.done);
  const setupSteps: TournamentSetupStep[] = [
    ...coreSetupSteps,
    {
      id: 'publish',
      label: 'Publish',
      done: isPublished,
      hint: isPublished
        ? `Status: ${tournament.status.replace('_', ' ')}.`
        : canPublish
        ? 'Ready to publish!'
        : `Waiting on: ${missing.join(', ')}.`,
      actionLabel: isPublished ? 'View public page' : canPublish ? 'Publish' : 'Finish blockers',
      href: isPublished
        ? paths.event(tournament.slug)
        : canPublish
        ? undefined
        : nextCoreStep?.href ?? (nextCoreStep?.onClick ? undefined : competitionHome),
      onClick: isPublished ? undefined : canPublish ? onPublish : nextCoreStep?.onClick,
    },
  ];

  const completedCount = setupSteps.filter((s) => s.done).length;
  const totalSteps = setupSteps.length;
  const nextStep = setupSteps.find((step) => !step.done);
  const nextActions = setupSteps.filter((step) => !step.done).slice(0, 3);
  const commandCards = [
    {
      icon: Users,
      title: 'Teams and players',
      value: `${teams.length} team${teams.length === 1 ? '' : 's'}`,
      hint: `${eventRosterEntries.length} rostered player${eventRosterEntries.length === 1 ? '' : 's'} across ${divisions.length} division${divisions.length === 1 ? '' : 's'}.`,
      href: teamsHref,
      action: 'Manage teams',
    },
    {
      icon: CalendarDays,
      title: 'Schedule status',
      value: games.length > 0 ? `${submittedGames.length}/${games.length} final` : 'No games',
      hint: games.length > 0 ? `${liveGames.length} live, ${upcomingGames.length} upcoming.` : 'Generate the game schedule before operations work starts.',
      href: scheduleHref,
      action: games.length > 0 ? 'Open schedule' : 'Generate schedule',
    },
    {
      icon: ClipboardCheck,
      title: 'Crew coverage',
      value: games.length === 0 ? 'Waiting on schedule' : missingCrew.length > 0 ? `${missingCrew.length} gap${missingCrew.length === 1 ? '' : 's'}` : 'Covered',
      hint: conflicts.length > 0 ? `${conflicts.length} crew conflict${conflicts.length === 1 ? '' : 's'} flagged.` : 'Scorekeeper and referee coverage lives in Operations.',
      href: operationsHref,
      action: 'Open operations',
    },
    {
      icon: CreditCard,
      title: 'Registration and payments',
      value: registrationEnabled ? `${eventRegistrations.length} registration${eventRegistrations.length === 1 ? '' : 's'}` : 'Not configured',
      hint: eventPayments.length > 0 ? `${paidPayments.length}/${eventPayments.length} payment${eventPayments.length === 1 ? '' : 's'} succeeded.` : 'Registration setup and payment review stay in league modules.',
      href: eventPayments.length > 0 ? financialsHref : registrationHref,
      action: eventPayments.length > 0 ? 'Open financials' : 'Open registration',
    },
    {
      icon: MessageSquare,
      title: 'Recent comms',
      value: sentCommunications.length > 0 ? `${sentCommunications.length} sent` : 'No sends',
      hint: latestCommunication
        ? `Latest: ${latestCommunication.subject}${deliveryIssues.length > 0 ? ` · ${deliveryIssues.length} delivery issue${deliveryIssues.length === 1 ? '' : 's'}` : ''}.`
        : 'Event Comms shows sent history, delivery audit, and replies.',
      href: sentCommunications.length > 0 ? eventCommsHref : composeCommsHref,
      action: sentCommunications.length > 0 ? 'Review history' : 'Compose in Comms',
    },
    {
      icon: ListChecks,
      title: 'Next actions',
      value: nextActions.length > 0 ? `${nextActions.length} blocker${nextActions.length === 1 ? '' : 's'}` : 'Ready',
      hint: nextActions.length > 0 ? nextActions.map((step) => step.label).join(', ') : 'No setup blockers are currently open.',
      href: nextActions[0]?.href ?? competitionHome,
      action: nextActions[0]?.actionLabel ?? 'Review workspace',
    },
  ];

  return (
    <EventWorkspaceSection>
      {/* KPIs — only interesting once there are games */}
      {games.length > 0 && (
        <div className="grid grid--4">
          <Stat label="Divisions" value={divisions.length} hint="In this event" />
          <Stat label="Live now" value={liveGames.length} hint="In progress" />
          <Stat label="Upcoming" value={upcomingGames.length} hint="Still to play" />
          <Stat label="Final" value={submittedGames.length} hint="Submitted" />
        </div>
      )}

      {/* ── Inline Edit ───────────────────────────────────────────────────── */}
      <div className="card">
        <div className="card__head">
          <div>
            <h3 className="card__title">Event details</h3>
            <p className="card__sub">Name, dates, and tagline.</p>
          </div>
          {!editing && (
            <button className="btn btn--ghost btn--sm" onClick={openEdit}>
              <Edit2 size={13} /> Edit
            </button>
          )}
        </div>

        {editing ? (
          <div className="stack" style={{ gap: 14 }}>
            <div className="field">
              <label className="field__label">Name</label>
              <input className="input" value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div className="field">
              <label className="field__label">Tagline (optional)</label>
              <input className="input" value={editTagline} onChange={(e) => setEditTagline(e.target.value)} placeholder="Three days. Twelve-minute battles." />
            </div>
            <div className="grid grid--2">
              <div className="field">
                <label className="field__label">Start</label>
                <input type="datetime-local" className="input" value={editStart} onChange={(e) => setEditStart(e.target.value)} />
              </div>
              <div className="field">
                <label className="field__label">End</label>
                <input type="datetime-local" className="input" value={editEnd} onChange={(e) => setEditEnd(e.target.value)} />
              </div>
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button className="btn btn--primary btn--sm" onClick={saveEdit}>
                <Save size={13} /> Save
              </button>
              <button className="btn btn--ghost btn--sm" onClick={() => setEditing(false)}>
                <X size={13} /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="stack" style={{ gap: 6 }}>
            <div><span className="eyebrow">Name</span> <span style={{ fontWeight: 600, marginLeft: 8 }}>{tournament.name}</span></div>
            {tournament.tagline && <div><span className="eyebrow">Tagline</span> <span className="muted" style={{ marginLeft: 8 }}>{tournament.tagline}</span></div>}
            <div>
              <span className="eyebrow">Dates</span>
              <span className="muted" style={{ marginLeft: 8 }}>
                {new Date(tournament.startsAt).toLocaleDateString()} – {new Date(tournament.endsAt).toLocaleDateString()}
              </span>
            </div>
            <div><span className="eyebrow">Format</span> <span className="muted" style={{ marginLeft: 8 }}>{(tournament.format ?? 'tournament') === 'season' ? 'Season' : 'Tournament'}</span></div>
          </div>
        )}
      </div>

      {/* ── Setup Tracker ─────────────────────────────────────────────────── */}
      <div className="card">
        <div className="card__head">
          <div>
            <h3 className="card__title">
              <Shield size={15} style={{ verticalAlign: -2, marginRight: 6 }} />
              Setup progress
            </h3>
            <p className="card__sub">{completedCount} of {totalSteps} steps done.</p>
          </div>
          {/* Publish button lives here */}
          {!isPublished && (
            <button
              className="btn btn--primary btn--sm"
              disabled={!canPublish}
              onClick={onPublish}
              title={canPublish ? 'Publish event' : `Still needed: ${missing.join(', ')}`}
            >
              <Rocket size={13} /> {canPublish ? 'Publish event' : 'Not ready yet'}
            </button>
          )}
          {isPublished && (
            <span className="chip chip--ok">
              <Radio size={11} style={{ marginRight: 4 }} />
              {tournament.status.replace('_', ' ')}
            </span>
          )}
        </div>

        {/* Progress bar */}
        <div style={{ height: 6, background: 'var(--surface-line)', borderRadius: 999, marginBottom: 18, overflow: 'hidden' }}>
          <div
            style={{
              height: '100%',
              width: `${(completedCount / totalSteps) * 100}%`,
              background: completedCount === totalSteps ? 'var(--ok)' : 'var(--accent)',
              borderRadius: 999,
              transition: 'width 0.4s ease',
            }}
          />
        </div>

        <ol style={{ listStyle: 'none', padding: 0, margin: 0, gap: 8 }} className="stack" aria-label="Setup steps">
          {setupSteps.map((step, i) => (
            <li key={step.id}>
              <SetupProgressRow step={step} index={i} />
            </li>
          ))}
        </ol>
        {nextStep && (
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
            <SetupProgressAction step={nextStep} label={`Next: ${nextStep.actionLabel}`} primary />
          </div>
        )}

        {publishErr && (
          <div className="note note--warn" style={{ marginTop: 12 }}>
            <AlertTriangle size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
            Can't publish yet — still missing: {publishErr.join(', ')}.
          </div>
        )}
      </div>

      {/* ── Conflict callout ─────────────────────────────────────────────── */}
      {conflicts.length > 0 && (
        <div className="card" style={{ borderColor: 'var(--warn)' }}>
          <div className="card__head">
            <div>
              <h3 className="card__title" style={{ color: 'var(--warn)' }}>
                <AlertTriangle size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
                Crew conflicts ({conflicts.length})
              </h3>
              <p className="card__sub">Same person assigned to overlapping games.</p>
            </div>
            <Link to="operations" className="btn btn--ghost btn--sm">Resolve</Link>
          </div>
        </div>
      )}

      {/* ── Command center ───────────────────────────────────────────────── */}
      <section className="card">
        <div className="card__head">
          <div>
            <h3 className="card__title">Command center</h3>
            <p className="card__sub">Event-specific status with links into the owning workspace or league module.</p>
          </div>
        </div>
        <div className="grid grid--3">
          {commandCards.map((card) => (
            <CommandCenterCard key={card.title} {...card} />
          ))}
        </div>
      </section>

      <CompetitionLifecycleAction
        tournament={tournament}
        variant="zone"
        onComplete={(action) => {
          if (action === 'delete') {
            navigate((tournament.format ?? 'tournament') === 'season' ? paths.adminSeasons : paths.adminTournaments);
          }
        }}
      />
    </EventWorkspaceSection>
  );
}

function SetupProgressRow({ step, index }: { step: TournamentSetupStep; index: number }) {
  const content = (
    <>
      <span className={`setup-check-row__mark ${step.done ? 'is-complete' : ''}`}>
        {step.done ? <CheckCircle2 size={14} /> : index + 1}
      </span>
      <span>
        <strong>{step.label}</strong>
        <span className="muted">{step.hint}</span>
      </span>
      <span className="setup-check-row__action">
        {step.actionLabel} <ArrowUpRight size={13} />
      </span>
    </>
  );

  if (step.href) {
    return (
      <Link to={step.href} className="setup-check-row setup-check-row--with-action">
        {content}
      </Link>
    );
  }

  return (
    <button type="button" className="setup-check-row setup-check-row--button setup-check-row--with-action" onClick={step.onClick}>
      {content}
    </button>
  );
}

function SetupProgressAction({ step, label, primary = false }: { step: TournamentSetupStep; label: string; primary?: boolean }) {
  const className = `btn ${primary ? 'btn--primary' : 'btn--ghost'} btn--sm`;
  const content = (
    <>
      {label} <ArrowUpRight size={13} />
    </>
  );

  if (step.href) {
    return (
      <Link to={step.href} className={className}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" className={className} onClick={step.onClick}>
      {content}
    </button>
  );
}

function CommandCenterCard({
  icon: Icon,
  title,
  value,
  hint,
  href,
  action,
}: {
  icon: LucideIcon;
  title: string;
  value: string;
  hint: string;
  href: string;
  action: string;
}) {
  return (
    <Link to={href} className="command-card">
      <span className="command-card__icon"><Icon size={16} /></span>
      <span className="command-card__body">
        <span className="eyebrow">{title}</span>
        <strong>{value}</strong>
        <span className="muted">{hint}</span>
      </span>
      <span className="command-card__action">
        {action} <ArrowUpRight size={13} />
      </span>
    </Link>
  );
}

function Stat({ label, value, hint }: { label: string; value: number; hint: string }) {
  return (
    <div className="card">
      <div className="eyebrow">{label}</div>
      <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
      <div className="muted" style={{ fontSize: 12 }}>{hint}</div>
    </div>
  );
}

function toLocal(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocal(local: string): string {
  if (!local) return '';
  return new Date(local).toISOString();
}
